import React from 'react';

const toInt = (value) => parseInt(value, 10) || 0;

// A completed task counts as fully done even if no units were logged against it
// (e.g. it was checked off from the MCP server without stepping the counter up).
export const unitsDoneOf = (task) => {
  const done = toInt(task.pomodoros);
  return task.state === 'completed' ? Math.max(done, toInt(task.estimatedPomodoro)) : done;
};

export const progressOf = (task) => {
  const done = unitsDoneOf(task);
  const estimate = toInt(task.estimatedPomodoro);
  const pct = estimate > 0 ? Math.round((100 * done) / estimate) : 0;
  return { done, estimate, pct };
};

export const sumUnits = (tasks) =>
  tasks.reduce(
    (totals, task) => ({
      done: totals.done + unitsDoneOf(task),
      estimate: totals.estimate + toInt(task.estimatedPomodoro),
    }),
    { done: 0, estimate: 0 }
  );

// Sets every parent's units to the sum of its subtasks', depth first, so rows and the
// overview are right even when a parent's stored rollup is stale (it is otherwise only
// re-summed while its subtask panel is open). Mutates and returns `tasks`.
export const rollUpUnits = (tasks) => {
  const childrenOf = {};
  tasks.forEach(task => {
    (childrenOf[task.parentTaskId] = childrenOf[task.parentTaskId] || []).push(task);
  });
  const visit = (task) => {
    const children = childrenOf[task.id];
    if (!children) return;
    children.forEach(visit);
    task.pomodoros = String(children.reduce((n, child) => n + unitsDoneOf(child), 0));
    task.estimatedPomodoro = children.reduce((n, child) => n + toInt(child.estimatedPomodoro), 0);
  };
  (childrenOf['none'] || []).forEach(visit);
  return tasks;
};

// Maps a task to a visual tone: finished, over its estimate, nearly there, or underway.
export const toneOf = (task) => {
  const { pct } = progressOf(task);
  if (task.state === 'completed') return 'done';
  if (pct > 100) return 'over';
  if (pct >= 70) return 'good';
  return 'active';
};

export function ProgressBar({ task, size = 'md', showLabel = true }) {
  const { pct } = progressOf(task);
  const tone = toneOf(task);
  const width = tone === 'done' ? 100 : Math.min(pct, 100);
  const label = tone === 'done' ? 'Done' : pct + '%';
  return (
    <div className={`progress progress--${size} progress--${tone}`}>
      <div
        className="progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(pct, 100)}
        aria-label="Progress"
      >
        <div className="progress__fill" style={{ width: width + '%' }} />
      </div>
      {showLabel && <span className="progress__label">{label}</span>}
    </div>
  );
}

export function ProgressRing({ pct, size = 132, stroke = 12 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(pct, 100));
  return (
    <svg className="ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <defs>
        <linearGradient id="ring-gradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--grad-1)" />
          <stop offset="55%" stopColor="var(--grad-2)" />
          <stop offset="100%" stopColor="var(--grad-3)" />
        </linearGradient>
      </defs>
      <circle className="ring__track" cx={size / 2} cy={size / 2} r={radius} strokeWidth={stroke} fill="none" />
      <circle
        className="ring__fill"
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={stroke}
        fill="none"
        stroke="url(#ring-gradient)"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - clamped / 100)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}
